import cartAtom from "@/atoms/cart";
import categoriesAtom from "@/atoms/categories";
import { lastSyncedCartAtom } from "@/atoms/last-synced-cart";
import { showToast } from "@/components/feedback";
import { cartSyncEnabled } from "@/consts";
import { cartBackendService } from "@/lib/cart-backend-service";
import { Cart } from "@/types";
import { atom, useAtom, useAtomValue, useSetAtom } from "jotai";
import { useEffect, useRef } from "react";
import { useAuthStatus } from "./use-auth-status";

/** Global atom: whether a cart sync is pending (scheduled debounce) or in flight. */
export const cartSyncPendingAtom = atom(false);

/** Custom event to force an immediate flush of the cart debounce. */
export const CART_FLUSH_EVENT = "cart:flush";
export const CART_SYNC_DONE_EVENT = "cart:sync-done";

export function flushCartSync(): Promise<void> {
  if (!cartSyncEnabled) return Promise.resolve();

  return new Promise((resolve) => {
    function onDone() {
      window.removeEventListener(CART_SYNC_DONE_EVENT, onDone);
      clearTimeout(safetyTimeout);
      resolve();
    }
    const safetyTimeout = setTimeout(onDone, 10000);
    window.addEventListener(CART_SYNC_DONE_EVENT, onDone);
    window.dispatchEvent(new Event(CART_FLUSH_EVENT));
  });
}

export function useCartSync() {
  const [cart, setCart] = useAtom(cartAtom);
  const setLastSyncedCart = useSetAtom(lastSyncedCartAtom);
  const lastSyncedCartValue = useAtomValue(lastSyncedCartAtom);
  const categories = useAtomValue(categoriesAtom);
  const isAuthenticated = useAuthStatus();
  const setCartSyncPending = useSetAtom(cartSyncPendingAtom);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const isSyncingRef = useRef(false);
  const pendingSyncRef = useRef<(() => Promise<void>) | null>(null);
  const syncPromiseRef = useRef<Promise<void> | null>(null);

  const lastSyncedCartRef = useRef<Cart | null>(lastSyncedCartValue);
  const categoriesRef = useRef(categories);

  useEffect(() => { lastSyncedCartRef.current = lastSyncedCartValue; }, [lastSyncedCartValue]);
  useEffect(() => { categoriesRef.current = categories; }, [categories]);

  useEffect(() => {
    if (!cartSyncEnabled || !isAuthenticated) {
      setCartSyncPending(false);
      return;
    }

    const currentCart = cart;

    const syncFn = async () => {
      if (isSyncingRef.current) {
        pendingSyncRef.current = syncFn;
        return;
      }

      if (lastSyncedCartRef.current === null) {
        setCartSyncPending(false);
        return;
      }

      const syncedCart: Cart = lastSyncedCartRef.current;

      if (currentCart.items.length === 0 && (syncedCart.items?.length || 0) === 0) {
        setCartSyncPending(false);
        return;
      }

      isSyncingRef.current = true;
      setCartSyncPending(true);

      try {
        const syncedItems = syncedCart.items || [];

        const addedItems = currentCart.items.filter((item) => !syncedItems.find((si) => si.id === item.id));
        const removedItems = syncedItems.filter((item) => !currentCart.items.find((ci) => ci.id === item.id));
        const updatedItems = currentCart.items.filter((item) => {
          const syncedItem = syncedItems.find((si) => si.id === item.id);
          return syncedItem && syncedItem.quantity !== item.quantity;
        });

        if (addedItems.length === 0 && removedItems.length === 0 && updatedItems.length === 0) {
          isSyncingRef.current = false;
          setCartSyncPending(false);
          return;
        }

        let addedCount = 0;
        let removedCount = 0;

        addedCount += addedItems.reduce((acc, item) => acc + item.quantity, 0);
        removedCount += removedItems.reduce((acc, item) => acc + item.quantity, 0);

        for (const item of updatedItems) {
          const syncedItem = syncedItems.find((si) => si.id === item.id);
          if (syncedItem) {
            const diff = item.quantity - syncedItem.quantity;
            if (diff > 0) addedCount += diff;
            else removedCount += Math.abs(diff);
          }
        }

        for (const item of removedItems) {
          await cartBackendService.removeCartItem(item.id);
        }

        for (const item of updatedItems) {
          await cartBackendService.updateCartItemQuantity(item.id, item.quantity);
        }

        const newIdMap = new Map<string, string>();

        for (const item of addedItems) {
          const response: any = await cartBackendService.saveCart(
            {
              ...currentCart,
              items: [item],
            },
            categoriesRef.current
          );

          const realId = response.id;
          newIdMap.set(item.id, realId);
        }

        if (newIdMap.size > 0) {
          const newSyncedItems = currentCart.items.map((i) => {
            if (newIdMap.has(i.id)) {
              return { ...i, id: newIdMap.get(i.id)! };
            }
            return i;
          });

          const updated = { ...currentCart, items: newSyncedItems };
          lastSyncedCartRef.current = updated;
          setLastSyncedCart(updated);

          setCart((prev) => ({
            ...prev,
            items: prev.items.map((i) => {
              if (newIdMap.has(i.id)) {
                return { ...i, id: newIdMap.get(i.id)! };
              }
              return i;
            }),
          }));
        } else {
          lastSyncedCartRef.current = currentCart;
          setLastSyncedCart(currentCart);
        }

        if (addedCount > 0) {
          showToast.dismiss();
          showToast.success(
            addedCount === 1
              ? "1 item adicionado à sacola"
              : `${addedCount} itens adicionados à sacola`
          );
        }
        if (removedCount > 0) {
          showToast.dismiss();
          showToast.success(
            removedCount === 1
              ? "1 item removido da sacola"
              : `${removedCount} itens removidos da sacola`
          );
        }
      } catch (error) {
        console.error("Erro ao sincronizar carrinho:", error);
      } finally {
        isSyncingRef.current = false;
        setCartSyncPending(false);

        if (pendingSyncRef.current && !debounceRef.current) {
          debounceRef.current = setTimeout(() => {
            debounceRef.current = null;
            if (pendingSyncRef.current) {
              const pendingFn = pendingSyncRef.current;
              pendingSyncRef.current = null;
              const p = pendingFn();
              syncPromiseRef.current = p;
              p.finally(() => { syncPromiseRef.current = null; });
            }
          }, 2000);
        }
      }
    };

    pendingSyncRef.current = syncFn;

    if (isSyncingRef.current || debounceRef.current) {
      return;
    }

    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      pendingSyncRef.current = null;
      const p = syncFn();
      syncPromiseRef.current = p;
      p.finally(() => { syncPromiseRef.current = null; });
    }, 2000);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart, isAuthenticated]);

  useEffect(() => {
    if (!cartSyncEnabled) return;

    function handleFlush() {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      if (pendingSyncRef.current) {
        const fn = pendingSyncRef.current;
        pendingSyncRef.current = null;
        const p = fn();
        syncPromiseRef.current = p;
        p.finally(() => {
          syncPromiseRef.current = null;
          window.dispatchEvent(new Event(CART_SYNC_DONE_EVENT));
        });
      } else if (syncPromiseRef.current) {
        syncPromiseRef.current.finally(() => {
          window.dispatchEvent(new Event(CART_SYNC_DONE_EVENT));
        });
      } else {
        window.dispatchEvent(new Event(CART_SYNC_DONE_EVENT));
      }
    }

    window.addEventListener(CART_FLUSH_EVENT, handleFlush);
    return () => window.removeEventListener(CART_FLUSH_EVENT, handleFlush);
  }, []);
}
